import React, { useState, useEffect } from "react";
import {
  Box, Typography, Grid, Card, TextField, Button, Divider, MenuItem,
  Snackbar, Alert, CircularProgress, Chip, Paper, IconButton,
} from "@mui/material";
import {
  Save as SaveIcon, ArrowBack as ArrowBackIcon, ArrowForwardIos,
  CheckCircle, Layers, Memory, Storage, Code, AutoAwesome, Security, Check,
} from "@mui/icons-material";
import Sidenav from "../../common/Sidenav";
import Header from "../../common/Header";
import { useNavigate } from "react-router-dom";
import { apiList, invokeFormDataApi, invokeApi } from "../../../services/apiServices";
import { config } from "../../../config/config";
import { useCookies } from "react-cookie";

export const PRODUCT_TAXONOMY = [
  {
    id: 1,
    title: "Windows",
    key: "windows",
    parent_category: "microsoft",
    icon: <Memory sx={{ fontSize: 20 }} />,
    subcategories: [
      { key: "windows-7", label: "Windows 7 (Pro, Ultimate, Home)" },
      { key: "windows-8", label: "Windows 8 (Standard, Pro)" },
      { key: "windows-8-1", label: "Windows 8.1 (Pro, Standard)" },
      { key: "windows-10", label: "Windows 10 (Pro, Home, Enterprise)" },
      { key: "windows-11", label: "Windows 11 (Pro, Home, Enterprise)" },
    ],
  },
  {
    id: 2,
    title: "MS Office",
    key: "ms-office",
    parent_category: "microsoft",
    icon: <Layers sx={{ fontSize: 20 }} />,
    subcategories: [
      { key: "office-2016", label: "Office 2016 (Home & Student, Pro Plus)" },
      { key: "office-2019", label: "Office 2019 (Pro Plus, Home & Business)" },
      { key: "office-2021", label: "Office 2021 (Pro Plus, Home & Business)" },
      { key: "office-2024", label: "Office 2024 (Pro Plus, Home & Business)" },
      { key: "office-365", label: "Microsoft 365 (Personal, Family, Business)" },
    ],
  },
  {
    id: 3,
    title: "Servers",
    key: "servers",
    parent_category: "microsoft",
    icon: <Storage sx={{ fontSize: 20 }} />,
    subcategories: [
      { key: "server-2012", label: "Windows Server 2012 R2" },
      { key: "server-2016", label: "Windows Server 2016 Standard/Datacenter" },
      { key: "server-2019", label: "Windows Server 2019 Standard/Datacenter" },
      { key: "server-2022", label: "Windows Server 2022 Standard/Datacenter" },
      { key: "server-2025", label: "Windows Server 2025 Standard/Datacenter" },
    ],
  },
  {
    id: 4,
    title: "SQL Server",
    key: "sql-server",
    parent_category: "microsoft",
    icon: <Storage sx={{ fontSize: 20 }} />,
    subcategories: [
      { key: "sql-server-2016", label: "SQL Server 2016 Standard" },
      { key: "sql-server-2017", label: "SQL Server 2017 Standard" },
      { key: "sql-server-2019", label: "SQL Server 2019 Standard" },
      { key: "sql-server-2022", label: "SQL Server 2022 Standard" },
    ],
  },
  {
    id: 5,
    title: "Visual Studio",
    key: "visual-studio",
    parent_category: "microsoft",
    icon: <Code sx={{ fontSize: 20 }} />,
    subcategories: [
      { key: "visual-studio-pro", label: "Visual Studio Professional 2022" },
      { key: "visual-studio-enterprise", label: "Visual Studio Enterprise 2022" },
    ],
  },
  {
    id: 6,
    title: "Adobe",
    key: "adobe",
    parent_category: "adobe",
    icon: <AutoAwesome sx={{ fontSize: 20 }} />,
    subcategories: [
      { key: "adobe-acrobat", label: "Adobe Acrobat Pro DC" },
      { key: "adobe-photoshop", label: "Adobe Photoshop" },
      { key: "adobe-illustrator", label: "Adobe Illustrator" },
      { key: "adobe-premiere", label: "Adobe Premiere Pro" },
      { key: "adobe-creative-cloud", label: "Adobe Creative Cloud All Apps" },
    ],
  },
  {
    id: 7,
    title: "Others",
    key: "others",
    parent_category: "others",
    icon: <Security sx={{ fontSize: 20 }} />,
    subcategories: [
      { key: "antivirus", label: "Antivirus (Quick Heal, Kaspersky, Norton, McAfee)" },
      { key: "auto-desk", label: "Auto Desk (AutoCAD, Revit, 3ds Max)" },
      { key: "coreldraw", label: "Corel Draw (Graphics Suite, Technical Suite)" },
      { key: "tally", label: "Tally (Tally Prime Silver, Tally Prime Gold)" },
    ],
  },
  {
    id: 8,
    title: "Combos",
    key: "combo",
    parent_category: "combo",
    icon: <Layers sx={{ fontSize: 20 }} />,
    subcategories: [
      { key: "combo-win-office", label: "Windows + Office Bundle" },
      { key: "combo-enterprise", label: "Enterprise Software Pack" },
    ],
  },
];

const AddProduct = () => {
  const navigate = useNavigate();
  const [cookies] = useCookies();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Currently viewed category in the side-by-side navigator
  const [activeTaxonomyCat, setActiveTaxonomyCat] = useState(PRODUCT_TAXONOMY[0]);

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    price: "",
    oldPrice: "",
    category: "Windows",
    category_id: 1,
    parent_category: "microsoft",
    subcategory: "windows-11",
    description: "",
    rating: "5.0",
    quantity: "999",
    reviews: "100",
    validity: "Lifetime",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategorySelect = (taxCat) => {
    setActiveTaxonomyCat(taxCat);
    const defaultSub = taxCat.subcategories[0]?.key || "";
    setFormData((prev) => ({
      ...prev,
      category: taxCat.title,
      category_id: taxCat.id,
      parent_category: taxCat.parent_category,
      subcategory: defaultSub,
    }));
  };

  const handleSubcategorySelect = (sub) => {
    setFormData((prev) => ({
      ...prev,
      category: activeTaxonomyCat.title,
      category_id: activeTaxonomyCat.id,
      parent_category: activeTaxonomyCat.parent_category,
      subcategory: sub.key,
    }));
  };

  const generateSlug = (name) => name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  const handleNameChange = (e) => {
    const val = e.target.value;
    setFormData((prev) => ({ ...prev, name: val, slug: generateSlug(val) }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.price) {
      setSnackbar({ open: true, message: "Please fill in product name and sale price.", severity: "warning" });
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        data.append(key, formData[key]);
      });
      if (selectedFile) data.append("image_file", selectedFile);

      try {
        await invokeFormDataApi(config.apiUrl + apiList.addProduct, data, cookies);
      } catch (err) {
        console.warn("Backend addProduct warning:", err);
      }

      // Persist to custom products so it immediately appears across the frontend and dashboard
      const newCustomProduct = {
        id: Date.now(),
        name: formData.name,
        slug: formData.slug || generateSlug(formData.name),
        price: parseFloat(formData.price) || 0,
        oldPrice: parseFloat(formData.oldPrice) || (parseFloat(formData.price) * 1.5),
        description: formData.description,
        category: formData.category,
        category_id: formData.category_id,
        parent_category: formData.parent_category,
        subcategory: formData.subcategory,
        validity: formData.validity || "Lifetime",
        rating: parseFloat(formData.rating) || 5.0,
        reviews: parseInt(formData.reviews, 10) || 50,
        quantity: parseInt(formData.quantity, 10) || 999,
        image: previewUrl || "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=400&q=80",
        tags: [formData.category.toLowerCase(), formData.subcategory.toLowerCase(), formData.parent_category.toLowerCase()],
        created_at: new Date().toISOString(),
      };

      try {
        const existingCustom = JSON.parse(localStorage.getItem("ar_custom_products") || "[]");
        localStorage.setItem("ar_custom_products", JSON.stringify([newCustomProduct, ...existingCustom]));
      } catch (e) {}

      setSnackbar({ open: true, message: `Product "${formData.name}" created successfully!`, severity: "success" });
      setTimeout(() => navigate("/inventory-manager/product-list"), 1500);
    } catch (error) {
      setSnackbar({ open: true, message: "Server error: " + error.message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const activeSubcategoryObj = activeTaxonomyCat.subcategories.find((s) => s.key === formData.subcategory);

  return (
    <Box sx={{ display: "flex", bgcolor: "#F8FAFC", minHeight: "100vh" }}>
      <Sidenav />
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, overflowX: "hidden" }}>
        <Header />
        <Box sx={{ mt: 2, maxWidth: 1100, mx: "auto" }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
            <Button
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIcon />}
              sx={{ color: "#0E6ADA", textTransform: "none", fontWeight: 700 }}
            >
              Back to Inventory
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 800, color: "#0E3A7A", fontFamily: "'Inter', sans-serif" }}>
              Add New Product
            </Typography>
          </Box>

          <form onSubmit={handleSave}>
            <Card elevation={0} sx={{ p: { xs: 2.5, md: 4 }, border: "1px solid #E2E8F0", borderRadius: 3 }}>
              <Grid container spacing={3}>
                {/* ── 1. Basic Info ── */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: "#0E3A7A", fontWeight: 800, mb: 1 }}>
                    1. Basic Information
                  </Typography>
                  <Divider />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Product Name *"
                    name="name"
                    value={formData.name}
                    onChange={handleNameChange}
                    required
                    placeholder="e.g. Windows 11 Pro Lifetime Retail License"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Slug (URL Friendly)"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    required
                    helperText="Auto-generated from product name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Product Description *"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    placeholder="Provide full description of features, compatibility, architecture, and activation guidelines."
                  />
                </Grid>

                {/* ── 2. Category & Subcategory Hierarchy Selector ── */}
                <Grid item xs={12}>
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="h6" sx={{ color: "#0E3A7A", fontWeight: 800 }}>
                      2. Category & Subcategory Selection
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Select primary category on the left, then click the right arrow to view and pick the exact edition/subcategory on the right
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2.5 }} />

                  {/* Active Selection Banner */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 2.5,
                      bgcolor: "#EFF6FF",
                      border: "1.5px solid #BFDBFE",
                      borderRadius: 2.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <CheckCircle sx={{ color: "#0E6ADA", fontSize: 26 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: "#1E40AF", fontWeight: 700, textTransform: "uppercase" }}>
                          Selected Taxonomy Path
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 800, color: "#0E3A7A" }}>
                          {formData.category} &nbsp;➔&nbsp; {activeSubcategoryObj?.label || formData.subcategory}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={`Parent: ${formData.parent_category.toUpperCase()}`}
                      size="small"
                      sx={{ bgcolor: "#DBEAFE", color: "#1E40AF", fontWeight: 700 }}
                    />
                  </Paper>

                  {/* Side-by-Side Category & Subcategory Navigator */}
                  <Paper
                    elevation={0}
                    sx={{
                      border: "1px solid #CBD5E1",
                      borderRadius: 2.5,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      minHeight: 280,
                    }}
                  >
                    {/* Left Side: Categories with Right Arrow Buttons */}
                    <Box
                      sx={{
                        width: { xs: "100%", md: "38%" },
                        borderRight: { md: "1px solid #E2E8F0" },
                        borderBottom: { xs: "1px solid #E2E8F0", md: "none" },
                        bgcolor: "#F8FAFC",
                        p: 1.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          color: "#64748B",
                          px: 1.5,
                          py: 0.5,
                          display: "block",
                          letterSpacing: 0.5,
                          textTransform: "uppercase",
                        }}
                      >
                        Categories
                      </Typography>
                      {PRODUCT_TAXONOMY.map((cat) => {
                        const isSelected = activeTaxonomyCat.key === cat.key;
                        return (
                          <Box
                            key={cat.key}
                            onClick={() => handleCategorySelect(cat)}
                            sx={{
                              p: 1.5,
                              my: 0.5,
                              borderRadius: 2,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              bgcolor: isSelected ? "#0E6ADA" : "transparent",
                              color: isSelected ? "#FFFFFF" : "#1E293B",
                              transition: "all 0.2s",
                              "&:hover": {
                                bgcolor: isSelected ? "#0E6ADA" : "#E2E8F0",
                              },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                              <Box sx={{ color: isSelected ? "#FFF" : "#0E6ADA", display: "flex" }}>
                                {cat.icon}
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: isSelected ? 800 : 600 }}>
                                {cat.title}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              sx={{
                                color: isSelected ? "#FFF" : "#64748B",
                                p: 0.5,
                              }}
                            >
                              <ArrowForwardIos sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Box>
                        );
                      })}
                    </Box>

                    {/* Right Side: Subcategories Grid */}
                    <Box sx={{ width: { xs: "100%", md: "62%" }, p: 2.5, bgcolor: "#FFFFFF" }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0E3A7A" }}>
                          Subcategories for: {activeTaxonomyCat.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click to select subcategory
                        </Typography>
                      </Box>

                      <Grid container spacing={1.5}>
                        {activeTaxonomyCat.subcategories.map((sub) => {
                          const isSubSelected = formData.subcategory === sub.key && formData.category === activeTaxonomyCat.title;
                          return (
                            <Grid item xs={12} key={sub.key}>
                              <Box
                                onClick={() => handleSubcategorySelect(sub)}
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  border: isSubSelected ? "2px solid #0E6ADA" : "1px solid #E2E8F0",
                                  bgcolor: isSubSelected ? "#EFF6FF" : "#FFFFFF",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  transition: "all 0.2s",
                                  "&:hover": {
                                    borderColor: "#0E6ADA",
                                    bgcolor: isSubSelected ? "#EFF6FF" : "#F8FAFC",
                                  },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: isSubSelected ? 800 : 600,
                                    color: isSubSelected ? "#0E6ADA" : "#1E293B",
                                  }}
                                >
                                  {sub.label}
                                </Typography>
                                {isSubSelected ? (
                                  <Check sx={{ color: "#0E6ADA", fontSize: 20 }} />
                                ) : (
                                  <Chip label="Select" size="small" variant="outlined" sx={{ fontSize: 11 }} />
                                )}
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>

                {/* ── 3. Pricing & Specifications ── */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: "#0E3A7A", fontWeight: 800, mb: 1, mt: 1 }}>
                    3. Pricing & Inventory Details
                  </Typography>
                  <Divider />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Sale Price (₹) *"
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    placeholder="e.g. 499"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Original Price / MRP (₹)"
                    name="oldPrice"
                    type="number"
                    value={formData.oldPrice}
                    onChange={handleChange}
                    placeholder="e.g. 1999"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="License Validity"
                    name="validity"
                    value={formData.validity}
                    onChange={handleChange}
                    placeholder="e.g. Lifetime Permanent / 1 Year"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Rating (0.0 – 5.0)"
                    name="rating"
                    type="number"
                    inputProps={{ step: 0.1, min: 0, max: 5 }}
                    value={formData.rating}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Stock Quantity"
                    name="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Number of Reviews"
                    name="reviews"
                    type="number"
                    value={formData.reviews}
                    onChange={handleChange}
                  />
                </Grid>

                {/* ── 4. Image Upload ── */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: "#0E3A7A", fontWeight: 800, mb: 1, mt: 1 }}>
                    4. Product Image
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ p: 3, border: "2px dashed #CBD5E1", textAlign: "center", borderRadius: 2, bgcolor: "#F8FAFC" }}>
                    {previewUrl ? (
                      <Box sx={{ mb: 2 }}>
                        <Box
                          component="img"
                          src={previewUrl}
                          alt="Preview"
                          sx={{ maxWidth: 220, maxHeight: 180, objectFit: "contain", borderRadius: 1.5 }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        No image selected (Default category license banner will be applied)
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      component="label"
                      sx={{
                        borderColor: "#0E6ADA",
                        color: "#0E6ADA",
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 2,
                      }}
                    >
                      {previewUrl ? "Change Image" : "Upload Product Image"}
                      <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  sx={{ borderRadius: 2, textTransform: "none", px: 4 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  sx={{
                    bgcolor: "#0E6ADA",
                    px: 6,
                    py: 1.2,
                    borderRadius: 2,
                    fontWeight: 800,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#0b54b0" },
                  }}
                >
                  {loading ? "Saving Product..." : "Save Product"}
                </Button>
              </Box>
            </Card>
          </form>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddProduct;
